import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Calendar, Plus, Pencil, Trash2, Save, ChevronLeft, ChevronRight,
  Clock, Repeat, ExternalLink, AlertCircle, Eye, X, Camera, Globe
} from 'lucide-react';
import { Modal, getRoleLabel, UploadProgress, prepareImageFile, confirmDialog } from './shared';

const TZ_OPTIONS = [
  { tz: 'Europe/London', label: 'London' },
  { tz: 'Europe/Lisbon', label: 'Lissabon' },
  { tz: 'Atlantic/Canary', label: 'Kanaren' },
  { tz: 'Europe/Athens', label: 'Athen' },
  { tz: 'Europe/Istanbul', label: 'Istanbul' },
  { tz: 'Europe/Moscow', label: 'Moskau' },
  { tz: 'Asia/Dubai', label: 'Dubai' },
  { tz: 'Asia/Kolkata', label: 'Indien (Delhi)' },
  { tz: 'Asia/Bangkok', label: 'Bangkok' },
  { tz: 'Asia/Singapore', label: 'Singapur' },
  { tz: 'Asia/Hong_Kong', label: 'Hongkong' },
  { tz: 'Asia/Tokyo', label: 'Tokio' },
  { tz: 'Australia/Sydney', label: 'Sydney' },
  { tz: 'Pacific/Auckland', label: 'Auckland' },
  { tz: 'Africa/Johannesburg', label: 'Johannesburg' },
  { tz: 'America/Sao_Paulo', label: 'São Paulo' },
  { tz: 'America/New_York', label: 'New York' },
  { tz: 'America/Chicago', label: 'Chicago' },
  { tz: 'America/Denver', label: 'Denver' },
  { tz: 'America/Los_Angeles', label: 'Los Angeles' },
  { tz: 'America/Mexico_City', label: 'Mexiko-Stadt' },
];

const tzLabel = (tz) => (TZ_OPTIONS.find(o => o.tz === tz)?.label) || tz.split('/').pop().replace(/_/g, ' ');

// Convert a Berlin-local time (HH:MM on YYYY-MM-DD) into the target timezone
const berlinToTz = (dateStr, timeStr, targetTz) => {
  try {
    const guess = new Date(`${dateStr}T${timeStr}:00Z`);
    const asBerlin = new Date(guess.toLocaleString('sv-SE', { timeZone: 'Europe/Berlin' }).replace(' ', 'T') + 'Z');
    const instant = new Date(guess.getTime() + (guess.getTime() - asBerlin.getTime()));
    const time = instant.toLocaleTimeString('de-DE', { timeZone: targetTz, hour: '2-digit', minute: '2-digit' });
    const localDate = instant.toLocaleDateString('sv-SE', { timeZone: targetTz });
    const dayDiff = localDate > dateStr ? ' (+1 Tag)' : localDate < dateStr ? ' (−1 Tag)' : '';
    return time + dayDiff;
  } catch { return null; }
};

const EventTimezones = ({ ev, dateStr }) => {
  const tzs = (ev.timezones || []).filter(Boolean);
  if (tzs.length === 0 || !ev.event_time) return null;
  const d = dateStr || ev._virtualDate || ev.event_date;
  return (
    <div className="event-tz-list" data-testid={`event-tz-${ev.id}`}>
      <Globe size={11} />
      {tzs.map(tz => {
        const t = berlinToTz(d, ev.event_time, tz);
        if (!t) return null;
        return <span key={tz} className="event-tz-item">{tzLabel(tz)} {t}</span>;
      })}
    </div>
  );
};

export default function CalendarTab({ user, authHeaders, API_URL, isAdminOrCoach, events, fetchEvents, categories, focusEvent }) {
  const [calMonth, setCalMonth] = useState(new Date());
  const [selectedCalDay, setSelectedCalDay] = useState(null);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({ title: '', description: '', event_date: '', event_time: '', event_time_end: '', meeting_link: '', recurrence: 'none', visible_to: ['user', 'sponsor', 'coach', 'admin'], category: '', image_url: '', timezones: [] });
  const [lightboxImg, setLightboxImg] = useState(null);
  const [imgUploading, setImgUploading] = useState(false);
  const [imgProg, setImgProg] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [calCategoryFilter, setCalCategoryFilter] = useState('Alle');

  // Jump to the event's day when opened via the countdown header
  useEffect(() => {
    if (focusEvent?.date) {
      const [fy, fm, fd] = focusEvent.date.split('-').map(Number);
      setCalMonth(new Date(fy, fm - 1, 1));
      setSelectedCalDay(fd);
      setCalCategoryFilter('Alle');
    }
  }, [focusEvent]);

  const userRole = user?.role || 'user';
  const year = calMonth.getFullYear();
  const month = calMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
  const monthName = calMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  // Filter events by role visibility and category
  const visibleEvents = events.filter(ev => {
    const vt = ev.visible_to || ['user', 'sponsor', 'coach', 'admin'];
    if (!(vt.includes(userRole) || isAdminOrCoach)) return false;
    if (calCategoryFilter !== 'Alle' && (ev.category || '') !== calCategoryFilter) return false;
    return true;
  });

  const eventsByDate = {};
  const addToDate = (dateStr, ev, isVirtual) => {
    if (!eventsByDate[dateStr]) eventsByDate[dateStr] = [];
    eventsByDate[dateStr].push({ ...ev, _virtualDate: dateStr, _isVirtual: isVirtual });
  };

  visibleEvents.forEach(ev => {
    const excluded = ev.excluded_dates || [];
    if (!ev.recurrence || ev.recurrence === 'none') {
      if (!excluded.includes(ev.event_date)) addToDate(ev.event_date, ev, false);
    } else {
      const startDate = new Date(ev.event_date + 'T00:00:00');
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0);

      const stepDays = ev.recurrence === 'daily' ? 1 : ev.recurrence === 'weekly' ? 7 : ev.recurrence === 'biweekly' ? 14 : 0;

      if (stepDays > 0) {
        // Day-based recurrence (daily, weekly, biweekly)
        const startMs = startDate.getTime();
        const monthStartMs = monthStart.getTime();
        const monthEndMs = monthEnd.getTime() + 86400000;

        // Calculate first occurrence on or after monthStart
        let firstMs;
        if (startMs >= monthStartMs) {
          firstMs = startMs;
        } else {
          const diffMs = monthStartMs - startMs;
          const stepMs = stepDays * 86400000;
          const steps = Math.floor(diffMs / stepMs);
          firstMs = startMs + steps * stepMs;
          if (firstMs < monthStartMs) firstMs += stepMs;
        }

        let currentMs = firstMs;
        let safety = 0;
        while (currentMs <= monthEndMs && safety < 60) {
          if (currentMs >= startMs) {
            const d = new Date(currentMs);
            const ds = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
            if (!excluded.includes(ds)) addToDate(ds, ev, ds !== ev.event_date);
          }
          currentMs += stepDays * 86400000;
          safety++;
        }
      } else if (ev.recurrence === 'monthly') {
        let current = new Date(startDate);
        while (current < monthStart) {
          current.setMonth(current.getMonth() + 1);
        }
        if (current < startDate) current = new Date(startDate);
        let safety = 0;
        while (current <= monthEnd && safety < 13) {
          if (current >= monthStart && current >= startDate) {
            const ds = `${current.getFullYear()}-${String(current.getMonth()+1).padStart(2,'0')}-${String(current.getDate()).padStart(2,'0')}`;
            if (!excluded.includes(ds)) addToDate(ds, ev, ds !== ev.event_date);
          }
          current.setMonth(current.getMonth() + 1);
          safety++;
        }
      }
    }
  });

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const handleDayClick = (day) => {
    if (!isAdminOrCoach) return;
    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    setEventForm({ title: '', description: '', event_date: dateStr, event_time: '', event_time_end: '', meeting_link: '', recurrence: 'none', visible_to: ['user', 'sponsor', 'coach', 'admin'], category: '', image_url: '', timezones: [] });
    setShowCreateEvent(true);
    setFormError('');
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setFormLoading(true); setFormError('');
    try {
      if (editingEvent) {
        await axios.put(`${API_URL}/api/community/events/${editingEvent.id}`, eventForm, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      } else {
        await axios.post(`${API_URL}/api/community/events`, eventForm, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      }
      setEventForm({ title: '', description: '', event_date: '', event_time: '', event_time_end: '', meeting_link: '', recurrence: 'none', visible_to: ['user', 'sponsor', 'coach', 'admin'], category: '', image_url: '', timezones: [] });
      setShowCreateEvent(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (e) { setFormError(e.response?.data?.detail || 'Fehler'); }
    setFormLoading(false);
  };

  const openEditEvent = (ev) => {
    setEditingEvent(ev);
    setEventForm({ title: ev.title, description: ev.description || '', event_date: ev.event_date, event_time: ev.event_time || '', event_time_end: ev.event_time_end || '', meeting_link: ev.meeting_link || '', recurrence: ev.recurrence || 'none', visible_to: ev.visible_to || ['user', 'sponsor', 'coach', 'admin'], category: ev.category || '', image_url: ev.image_url || '', timezones: ev.timezones || [] });
    setShowCreateEvent(true);
    setFormError('');
  };

  const handleDeleteEvent = async (eventId, virtualDate, recurrence) => {
    if (recurrence && recurrence !== 'none' && virtualDate) {
      setDeleteDialog({ eventId, virtualDate, recurrence });
    } else {
      if (!(await confirmDialog('Termin wirklich löschen?', { title: 'Termin löschen' }))) return;
      try { await axios.delete(`${API_URL}/api/community/events/${eventId}`, { headers: authHeaders }); } catch (e) { console.error(e); }
      fetchEvents();
    }
  };

  const executeDelete = async (action) => {
    if (!deleteDialog) return;
    const { eventId, virtualDate } = deleteDialog;
    try {
      if (action === 'all') {
        await axios.delete(`${API_URL}/api/community/events/${eventId}`, { headers: authHeaders });
      } else if (action === 'this') {
        await axios.post(`${API_URL}/api/community/events/${eventId}/exclude?date=${virtualDate}`, {}, { headers: authHeaders });
      }
    } catch (e) { console.error(e); }
    setDeleteDialog(null);
    fetchEvents();
  };

  const selectedDateStr = selectedCalDay ? `${year}-${String(month+1).padStart(2,'0')}-${String(selectedCalDay).padStart(2,'0')}` : null;
  const selectedEvents = selectedDateStr ? (eventsByDate[selectedDateStr] || []) : [];

  const jumpToDate = (dateStr) => {
    const [jy, jm, jd] = dateStr.split('-').map(Number);
    setCalMonth(new Date(jy, jm - 1, 1));
    setSelectedCalDay(jd);
    setTimeout(() => document.querySelector('.cal-events-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const recurrenceLabels = { daily: 'Täglich', weekly: 'Wöchentlich', biweekly: 'Alle 2 Wochen', monthly: 'Monatlich' };
  const isRestricted = (ev) => {
    const vt = (ev.visible_to || ['user', 'sponsor', 'coach', 'admin']).filter(r => r !== 'admin');
    return vt.length < 3;
  };

  const toggleVisibleTo = (role) => {
    const vt = eventForm.visible_to || [];
    setEventForm({ ...eventForm, visible_to: vt.includes(role) ? vt.filter(r => r !== role) : [...vt, role] });
  };

  return (
    <div className="tab-content" data-testid="calendar-content">
      <div className="section-header">
        <h2><Calendar size={20} /> Kalender</h2>
        {isAdminOrCoach && <button onClick={() => { setEventForm({ title: '', description: '', event_date: '', event_time: '', event_time_end: '', meeting_link: '', recurrence: 'none', visible_to: ['user', 'sponsor', 'coach', 'admin'], category: '', image_url: '', timezones: [] }); setEditingEvent(null); setShowCreateEvent(true); setFormError(''); }} className="btn-sm" data-testid="create-event-btn"><Plus size={16} /> Neues Event</button>}
      </div>

      {/* Category filter */}
      {categories && categories.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <select value={calCategoryFilter} onChange={e => setCalCategoryFilter(e.target.value)} className="news-mobile-cat-select" style={{ maxWidth: '250px' }} data-testid="cal-category-filter">
            <option value="Alle">Alle Kategorien</option>
            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
      )}

      <div className="cal-container">
        <div className="cal-nav">
          <button onClick={() => { setCalMonth(new Date(year, month - 1, 1)); setSelectedCalDay(null); }} className="cal-nav-btn" data-testid="cal-prev"><ChevronLeft size={18} /></button>
          <span className="cal-month-label" data-testid="cal-month">{monthName}</span>
          <button onClick={() => { setCalMonth(new Date(year, month + 1, 1)); setSelectedCalDay(null); }} className="cal-nav-btn" data-testid="cal-next"><ChevronRight size={18} /></button>
          <button onClick={() => { setCalMonth(new Date()); setSelectedCalDay(null); }} className="cal-today-btn">Heute</button>
        </div>

        <div className="cal-grid">
          {['Mo','Di','Mi','Do','Fr','Sa','So'].map(d => <div key={d} className="cal-weekday">{d}</div>)}
          {cells.map((day, i) => {
            if (day === null) return <div key={`empty-${i}`} className="cal-cell empty" />;
            const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
            const dayEvents = eventsByDate[dateStr] || [];
            const hasEvents = dayEvents.length > 0;
            const hasRestricted = dayEvents.some(isRestricted);
            const isToday = dateStr === todayStr;
            const isSelected = day === selectedCalDay;
            return (
              <div key={day} className={`cal-cell ${isToday ? 'today' : ''} ${hasEvents ? 'has-event' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={() => { setSelectedCalDay(day); if (isAdminOrCoach && !hasEvents) handleDayClick(day); }}
                data-testid={`cal-day-${day}`}>
                <span className="cal-day-num">{day}</span>
                {hasEvents && <div className="cal-event-dots">
                  {dayEvents.slice(0, 3).map((ev, j) => <span key={j} className={`cal-dot ${isRestricted(ev) ? 'cal-dot-restricted' : ''}`} title={ev.title} />)}
                </div>}
              </div>
            );
          })}
        </div>

        <div className="cal-events-panel">
          {selectedCalDay ? (
            <>
              <div className="cal-panel-head">
                <h3>{selectedCalDay}. {calMonth.toLocaleDateString('de-DE', { month: 'long' })}</h3>
                <button type="button" className="cal-back-btn" onClick={() => setSelectedCalDay(null)} data-testid="cal-back-to-upcoming"><ChevronLeft size={14} /> Nächste Events</button>
              </div>
              {selectedEvents.length === 0 ? (
                <p className="hint-text">{isAdminOrCoach ? 'Klicke auf den Tag oben, um ein Event zu erstellen.' : 'Keine Events an diesem Tag.'}</p>
              ) : (
                selectedEvents.map(ev => (
                  <div key={ev.id} className={`event-card-mini ${isRestricted(ev) ? 'event-restricted' : ''} ${focusEvent?.id === ev.id ? 'event-focus-highlight' : ''}`} data-testid={`event-${ev.id}`}>
                    {ev.image_url && (
                      <img src={`${API_URL}${ev.image_url}`} alt={ev.title} className="event-thumb"
                        onClick={() => setLightboxImg(`${API_URL}${ev.image_url}`)} data-testid={`event-thumb-${ev.id}`} />
                    )}
                    <div className="event-info">
                      <h4>{ev.title}{ev.category && <span className="news-cat-badge" style={{marginLeft: '0.4rem', fontSize: '0.6rem'}}>{ev.category}</span>}</h4>
                      {(ev.event_time || ev.event_time_end) && (
                        <span className="event-time"><Clock size={12} /> {ev.event_time || '?'}{ev.event_time_end ? ` - ${ev.event_time_end}` : ''} Uhr{(ev.timezones || []).length > 0 ? ' (Berlin)' : ''}</span>
                      )}
                      <EventTimezones ev={ev} />
                      {ev.recurrence && ev.recurrence !== 'none' && <span className="recurrence-badge"><Repeat size={10} /> {recurrenceLabels[ev.recurrence] || ev.recurrence}</span>}
                      {isRestricted(ev) && <span className="event-visibility-badge"><Eye size={10} /> {(ev.visible_to || []).filter(r => r !== 'admin').map(r => getRoleLabel(r)).join(', ')}</span>}
                      {ev.description && <p>{ev.description}</p>}
                      {ev.meeting_link && <a href={ev.meeting_link} target="_blank" rel="noreferrer" className="event-link"><ExternalLink size={12} /> Meeting-Link</a>}
                    </div>
                    {isAdminOrCoach && <div className="event-actions-btns"><button className="btn-icon-sm" onClick={() => openEditEvent(ev)} title="Bearbeiten"><Pencil size={14} /></button><button className="btn-icon-sm danger" onClick={() => handleDeleteEvent(ev.id, ev._virtualDate || ev.event_date, ev.recurrence)}><Trash2 size={14} /></button></div>}
                  </div>
                ))
              )}
              {isAdminOrCoach && <button className="btn-sm cal-add-btn" onClick={() => handleDayClick(selectedCalDay)}><Plus size={14} /> Event hinzufuegen</button>}
            </>
          ) : (() => {
            const upcomingList = [];
            Object.keys(eventsByDate).sort().forEach(d => {
              if (d >= todayStr) eventsByDate[d].forEach(ev => upcomingList.push({ ...ev, displayDate: d }));
            });
            return (
              <>
                <h3>Nächste Events</h3>
                <p className="hint-text" style={{ marginTop: '-0.25rem' }}>Tippe auf ein Event, um Details und den Teilnahme-Link zu sehen.</p>
                {upcomingList.slice(0, 5).map((ev, i) => (
                  <div key={`${ev.id}-${ev.displayDate}-${i}`} className={`event-card-mini event-card-clickable ${isRestricted(ev) ? 'event-restricted' : ''}`}
                    role="button" tabIndex={0} title="Details anzeigen"
                    onClick={() => jumpToDate(ev.displayDate)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); jumpToDate(ev.displayDate); } }}
                    data-testid={`upcoming-event-${ev.id}-${ev.displayDate}`}>
                    <div className="event-date-sm">{new Date(ev.displayDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}</div>
                    {ev.image_url && (
                      <img src={`${API_URL}${ev.image_url}`} alt={ev.title} className="event-thumb"
                        onClick={(e) => { e.stopPropagation(); setLightboxImg(`${API_URL}${ev.image_url}`); }} data-testid={`upcoming-thumb-${ev.id}`} />
                    )}
                    <div className="event-info">
                      <h4>{ev.title}</h4>
                      {(ev.event_time || ev.event_time_end) && <span className="event-time"><Clock size={12} /> {ev.event_time || '?'}{ev.event_time_end ? ` - ${ev.event_time_end}` : ''} Uhr{(ev.timezones || []).length > 0 ? ' (Berlin)' : ''}</span>}
                      <EventTimezones ev={ev} dateStr={ev.displayDate} />
                      {ev.meeting_link && <span className="event-link event-link-hint"><ExternalLink size={12} /> Teilnahme-Link vorhanden</span>}
                    </div>
                    <ChevronRight size={16} className="event-card-chevron" />
                  </div>
                ))}
                {upcomingList.length === 0 && <p className="hint-text">Keine anstehenden Events.</p>}
              </>
            );
          })()}
        </div>
      </div>

      {/* Event Modal */}
      <Modal show={showCreateEvent} onClose={() => { setShowCreateEvent(false); setEditingEvent(null); }} title={editingEvent ? 'Event bearbeiten' : 'Neues Event erstellen'} preventBackdropClose>
        <form onSubmit={handleCreateEvent} className="modal-form">
          {formError && <div className="form-error"><AlertCircle size={16} /><span>{formError}</span></div>}
          <div className="form-group"><label>Titel</label><input value={eventForm.title} onChange={e => setEventForm({...eventForm, title: e.target.value})} required data-testid="event-title-input" /></div>
          <div className="form-group"><label>Beschreibung</label><textarea value={eventForm.description} onChange={e => setEventForm({...eventForm, description: e.target.value})} rows={3} /></div>
          <div className="form-row">
            <div className="form-group"><label>Datum</label><input type="date" value={eventForm.event_date} onChange={e => setEventForm({...eventForm, event_date: e.target.value})} required data-testid="event-date-input" /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Von (Uhrzeit)</label><input type="time" value={eventForm.event_time} onChange={e => setEventForm({...eventForm, event_time: e.target.value})} data-testid="event-time-input" /></div>
            <div className="form-group"><label>Bis (Uhrzeit)</label><input type="time" value={eventForm.event_time_end} onChange={e => setEventForm({...eventForm, event_time_end: e.target.value})} data-testid="event-time-end-input" /></div>
          </div>
          <div className="form-group">
            <label>Zusätzliche Zeitzonen (optional, max. 9) – Standard: Berlin</label>
            <select className="modal-select" value="" disabled={(eventForm.timezones || []).length >= 9} data-testid="event-tz-select"
              onChange={e => {
                const tz = e.target.value;
                if (tz && !(eventForm.timezones || []).includes(tz) && (eventForm.timezones || []).length < 9) {
                  setEventForm({ ...eventForm, timezones: [...(eventForm.timezones || []), tz] });
                }
              }}>
              <option value="">{(eventForm.timezones || []).length >= 9 ? 'Maximum erreicht (9)' : '+ Zeitzone hinzufügen…'}</option>
              {TZ_OPTIONS.filter(o => !(eventForm.timezones || []).includes(o.tz)).map(o => (
                <option key={o.tz} value={o.tz}>{o.label}</option>
              ))}
            </select>
            {(eventForm.timezones || []).length > 0 && (
              <div className="event-tz-chips" data-testid="event-tz-chips">
                {eventForm.timezones.map(tz => (
                  <span key={tz} className="event-tz-chip">
                    {tzLabel(tz)}
                    <button type="button" onClick={() => setEventForm({ ...eventForm, timezones: eventForm.timezones.filter(t => t !== tz) })} data-testid={`event-tz-remove-${tz.replace(/\//g, '-')}`}><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="form-group"><label>Meeting-Link (optional)</label><input value={eventForm.meeting_link} onChange={e => setEventForm({...eventForm, meeting_link: e.target.value})} placeholder="https://..." /></div>
          <div className="form-group">
            <label>Bild (optional)</label>
            {eventForm.image_url ? (
              <div className="event-img-preview">
                <img src={`${API_URL}${eventForm.image_url}`} alt="Event" />
                <button type="button" className="btn-icon-sm danger" onClick={() => setEventForm({...eventForm, image_url: ''})} data-testid="event-image-remove"><X size={14} /></button>
              </div>
            ) : (
              <label className="btn-sm" style={{ cursor: 'pointer', width: 'fit-content' }}>
                <Camera size={14} /> {imgUploading ? 'Lädt hoch...' : 'Bild auswählen'}
                <input type="file" accept="image/*" hidden data-testid="event-image-input" onChange={async (e) => {
                  const file = e.target.files[0];
                  const input = e.target;
                  if (!file) return;
                  let toUpload;
                  try { toUpload = await prepareImageFile(file); } catch (perr) { setFormError(perr.message); input.value = ''; return; }
                  setImgUploading(true);
                  try {
                    const fd = new FormData();
                    fd.append('file', toUpload);
                    const resp = await axios.post(`${API_URL}/api/community/events/upload-image`, fd, { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' }, onUploadProgress: ev => setImgProg({ name: toUpload.name, loaded: ev.loaded, total: ev.total || toUpload.size }) });
                    setEventForm(prev => ({ ...prev, image_url: resp.data.image_url }));
                  } catch (err) { setFormError(err.response?.data?.detail || 'Bild-Upload fehlgeschlagen'); }
                  setImgProg(null);
                  setImgUploading(false);
                  input.value = '';
                }} />
              </label>
            )}
            <UploadProgress progress={imgProg} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Wiederholung</label>
              <select value={eventForm.recurrence} onChange={e => setEventForm({...eventForm, recurrence: e.target.value})} className="modal-select" data-testid="event-recurrence">
                <option value="none">Keine</option>
                <option value="daily">Täglich</option>
                <option value="weekly">Wöchentlich</option>
                <option value="biweekly">Alle 2 Wochen</option>
                <option value="monthly">Monatlich</option>
              </select>
            </div>
            <div className="form-group">
              <label>Kategorie (optional)</label>
              <select value={eventForm.category || ''} onChange={e => setEventForm({...eventForm, category: e.target.value})} className="modal-select" data-testid="event-category">
                <option value="">Keine</option>
                {(categories || []).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Sichtbar für</label>
            <div className="checkbox-group">
              {['user', 'sponsor', 'coach'].map(role => (
                <label key={role} className="checkbox-label">
                  <input type="checkbox" checked={(eventForm.visible_to || []).includes(role)} onChange={() => toggleVisibleTo(role)} />
                  {getRoleLabel(role)}
                </label>
              ))}
            </div>
          </div>
          <button type="submit" className="btn-login" disabled={formLoading} data-testid="event-submit">{editingEvent ? <><Save size={16} /> {formLoading ? 'Speichern...' : 'Speichern'}</> : <><Plus size={16} /> {formLoading ? 'Erstellen...' : 'Event erstellen'}</>}</button>
        </form>
      </Modal>

      {/* Image Lightbox */}
      {lightboxImg && (
        <div className="cal-lightbox" onClick={() => setLightboxImg(null)} data-testid="event-lightbox">
          <img src={lightboxImg} alt="Event" onClick={e => e.stopPropagation()} />
          <button className="cal-lightbox-close" onClick={() => setLightboxImg(null)} data-testid="event-lightbox-close"><X size={22} /></button>
        </div>
      )}

      {/* Delete Series Dialog */}
      {deleteDialog && (
        <div className="modal-overlay" onClick={() => setDeleteDialog(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3>Serientermin löschen</h3>
              <button className="modal-close" onClick={() => setDeleteDialog(null)}><X size={18} /></button>
            </div>
            <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <p style={{ color: '#B8B8B8', fontSize: '0.88rem', margin: 0 }}>Was möchtest du löschen?</p>
              <button className="btn-sm" style={{ width: '100%', justifyContent: 'center', padding: '0.7rem' }} onClick={() => executeDelete('this')} data-testid="delete-this">
                <Trash2 size={14} /> Nur diesen Termin ausschliessen
              </button>
              <button className="btn-sm" style={{ width: '100%', justifyContent: 'center', padding: '0.7rem', background: 'rgba(255,0,85,0.1)', borderColor: 'rgba(255,0,85,0.3)', color: '#FF0055' }} onClick={() => executeDelete('all')} data-testid="delete-all">
                <Trash2 size={14} /> Gesamte Serie löschen
              </button>
              <button className="btn-icon" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setDeleteDialog(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
